import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '~/i18n';
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  TextField,
  useTheme,
} from '@material-ui/core';
import {
  DeleteOutlined,
  CheckCircleOutlineOutlined,
  ArrowBackOutlined,
  ArrowForwardOutlined,
  Warning,
} from '@material-ui/icons';
import PropTypes from 'prop-types';
import PF2MSelectOption from '~/components/PF2MSelectOption';
import PF2MGenericButton from '~/components/PF2MGenericButton';
import { convertTimeStampToMoment } from '~/utils/moment';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';
import { setHighlightEquipmentStatus, updateEquipmentStatus } from '~/store/dispatch/actions';
import { STATUS_COMMENT_CHARACTER_LIMIT } from '../constants';
import {
  buildOperatorsOptions,
  buildElementsOptions,
  buildSubElementsOptions,
  buildCodesOptions,
  getSelectedCodeOptionValue,
  buildMaintenanceCodesOptions,
  hasMaintenanceConfig,
  isFormValid,
  hasFormChanged,
  isSelectedCodeInvisible,
  applyFieldCascade,
  calculateMinEditDate,
  getDateErrorMessage,
  clearInvalidOperator,
} from './utils';
import PF2MDatepickerNew from '~/components/PF2MDatepickerNew';

const StatusTab = ({
  classes,
  currentModalStates,
  setStatusState,
  lookupCtx,
  onClose,
}) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const format = i18n.t('date:DateFormat');

  const {
    form = {},
    originalForm = {},
    dateError = null,
    isConfirmChangesStep = false,
    checkboxValue,
  } = currentModalStates || {};

  const {
    config,
    closures,
    userAccessLevel,
    operatorsById,
    codes,
    codesByKey,
    elementsById,
    subElementsById,
    maintenanceCodesById,
  } = lookupCtx || {};

  useEffect(() => {
    if (!operatorsById || Object.keys(operatorsById).length === 0 || !form?.id_equip) return;

    setStatusState({
      form: clearInvalidOperator(form, operatorsById),
      originalForm: clearInvalidOperator(originalForm, operatorsById),
    });
  }, [operatorsById]);

  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(
    userAccessLevel,
  );
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const minDateLimit = calculateMinEditDate(
    userLimitEditConfig,
    enableClosure,
    closures,
  );

  const defaultOption = useMemo(
    () => ({ value: 0, label: translate('common:Select') }),
    [translate],
  );

  const operatorsOptions = useMemo(
    () => [defaultOption, ...buildOperatorsOptions(operatorsById, form)],
    [defaultOption, operatorsById, form],
  );

  const elementsOptions = useMemo(
    () => [defaultOption, ...buildElementsOptions(elementsById)],
    [defaultOption, elementsById],
  );

  const subElementsOptions = useMemo(
    () => [defaultOption, ...buildSubElementsOptions(subElementsById, form)],
    [defaultOption, subElementsById, form],
  );

  const codesOptions = useMemo(
    () => [defaultOption, ...buildCodesOptions(codes, form)],
    [defaultOption, codes, form],
  );

  const selectedCodeOptionValue = useMemo(
    () => getSelectedCodeOptionValue(codesByKey, form),
    [codesByKey, form],
  );

  const maintenanceCodesOptions = useMemo(
    () => [defaultOption, ...buildMaintenanceCodesOptions(maintenanceCodesById)],
    [defaultOption, maintenanceCodesById],
  );

  const showMaintenanceField = useMemo(
    () => hasMaintenanceConfig(config),
    [config],
  );

  const formIsValid = useMemo(
    () => isFormValid(dateError, form, lookupCtx),
    [dateError, form, lookupCtx],
  );

  const hasChanged = useMemo(
    () => hasFormChanged(form, originalForm),
    [form, originalForm],
  );

  const isInvisibleCode = useMemo(
    () => isSelectedCodeInvisible(form, codesByKey),
    [form, codesByKey],
  );

  const handleFieldChange = field => (value) => {
    if (field === 'comment' && value?.length > STATUS_COMMENT_CHARACTER_LIMIT) return;

    const nextForm = applyFieldCascade(form, field, value, lookupCtx);

    if (field === 'start_timestamp') {
      setStatusState({
        form: nextForm,
        dateError: getDateErrorMessage(value, minDateLimit, translate),
      });
      return;
    }

    setStatusState({ form: nextForm });
  };

  const handleDiscard = () => {
    setStatusState({
      form: originalForm,
      dateError: null,
      isConfirmChangesStep: false,
    });
  };

  const handleSave = () => {
    if (!formIsValid || !hasChanged) {
      onClose();
      return;
    }

    if (checkboxValue === 0) {
      form.sent_from_tablet = true;
    }

    dispatch(updateEquipmentStatus({
      equipmentStatus: form,
      finallyCb: () => {
        dispatch(setHighlightEquipmentStatus([form?.id_equip]));
      },
    }));

    onClose();
  };

  const handleBack = () => {
    setStatusState({ isConfirmChangesStep: false });
  };

  const handleContinue = () => {
    setStatusState({ isConfirmChangesStep: true });
  };


  if (isConfirmChangesStep) {
    const confirmMessages = isInvisibleCode
      ? [
        { label: translate('common:InvisibleCodeAlert') },
        { label: translate('common:OnlyVisibleOnPlatform'), style: { fontWeight: 500 } },
        { label: translate('common:ToSendTurnOnVisible') },
      ]
      : [
        { label: translate('common:ChangeWillBeSent') },
        { label: translate('common:OperatorWillReceiveMessageAlert'), style: { fontWeight: 500 } },
        { label: translate('common:DoYouWantToContinue') },
      ];

    return (
      <div className={classes.wrapperConfirmAlert}>
        <div className={classes.confirmAlert}>
          <div className={classes.confirmAlertHeader}>
            <Warning fontSize="small" className={classes.confirmAlertIcon} />
            <Typography className={classes.confirmAlertTitle}>
              {translate('common:AttentionAlert')}
            </Typography>
          </div>
          <div className={classes.confirmAlertMessages}>
            {confirmMessages.map(message => (
              <Typography
                key={message.label}
                className={classes.confirmAlertText}
                style={message.style || {}}
              >
                {message.label}
              </Typography>
            ))}
          </div>
        </div>
        <div className={classes.formActions}>
          <PF2MGenericButton
            className={classes.buttonActions}
            onClick={handleBack}
            startIcon={<ArrowBackOutlined fontSize="small" />}
          >
            {translate('common:Back')}
          </PF2MGenericButton>
          <PF2MGenericButton
            variant="contained"
            color="secondary"
            className={classes.buttonActions}
            onClick={handleSave}
            startIcon={<CheckCircleOutlineOutlined fontSize="small" />}
          >
            {translate('common:Commit Changes')}
          </PF2MGenericButton>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wrapperChildren}>
      <div className={classes.boxFields}>
        <PF2MSelectOption
          description={`${translate('common:Code')} *`}
          options={codesOptions}
          boxDescriptionClassName={classes.boxLabel}
          descriptionClassName={classes.labelText}
          onChange={e => handleFieldChange('id_code')(e.target.value)}
          value={selectedCodeOptionValue}
        />
        {showMaintenanceField
          && (
            <PF2MSelectOption
              description={translate('common:Task')}
              options={maintenanceCodesOptions}
              boxDescriptionClassName={classes.boxLabel}
              descriptionClassName={classes.labelText}
              onChange={e => handleFieldChange('code_maintenance_id')(e.target.value)}
              value={form?.code_maintenance_id || 0}
              disabled={form?.code_type !== 0}
            />
          )
        }
        <PF2MSelectOption
          description={`${translate('common:Operator')} *`}
          options={operatorsOptions}
          boxDescriptionClassName={classes.boxLabel}
          descriptionClassName={classes.labelText}
          onChange={e => handleFieldChange('id_operator')(e.target.value)}
          value={form?.id_operator}
        />
        <div className={classes.wrapperDateTimePickerLabel}>
          <Typography className={classes.labelText}>{`${translate('common:DateAndTime')} *`}</Typography>
          <PF2MDatepickerNew
            isFormControlled={false}
            value={convertTimeStampToMoment(form?.start_timestamp)}
            onChange={value => handleFieldChange('start_timestamp')(value)}
            disableFuture
            isDateTime
            format={`${format} HH:mm:ss`}
            views={['hours', 'minutes', 'seconds']}
            noParseDate
            minDate={minDateLimit}
            data-testid="date-time-picker"
            error={!!dateError}
            helperText={dateError}
            style={{
              width: '100%',
              border: dateError ? '1px solid red' : `1px solid ${theme.palette.gray[20]}`,
              borderRadius: 4,
              padding: '6px 8px',
              height: '40px',
            }}
            inputProps={{
              style: {
                fontSize: '14px',
                lineHeight: '20px',
                color: theme.palette.gray[60],
              },
            }}
          />
        </div>
        <PF2MSelectOption
          description={translate('common:Element')}
          options={elementsOptions}
          boxDescriptionClassName={classes.boxLabel}
          descriptionClassName={classes.labelText}
          onChange={e => handleFieldChange('element_id')(e.target.value)}
          value={form?.element_id}
        />
        <PF2MSelectOption
          description={translate('common:SubElement')}
          options={subElementsOptions}
          boxDescriptionClassName={classes.boxLabel}
          descriptionClassName={classes.labelText}
          onChange={e => handleFieldChange('point_id')(e.target.value)}
          value={form?.point_id}
          disabled={!form?.element_id || form?.element_id === 0}
        />

      </div>
      <div className={classes.wrapperTextFieldLabel}>
        <Typography className={classes.labelText}>{translate('common:Comment')}</Typography>
        <TextField
          placeholder={translate('common:EnterText')}
          value={form?.comment}
          onChange={e => handleFieldChange('comment')(e.target.value)}
          variant="outlined"
          multiline
          minRows={3}
          maxRows={3}
          id="text-field"
        />
      </div>
      {checkboxValue > 0 && (
        <div className={classes.wrapperCheckbox}>
          <FormGroup>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={form?.sent_from_tablet || false}
                  classes={{
                    root: classes.checkboxRoot,
                  }}
                />
              )}
              label={translate('common:DefinitiveNote')}
              classes={{
                label: classes.labelCheckbox,
                root: classes.formControlRoot,
              }}
              onChange={e => handleFieldChange('sent_from_tablet')(e.target.checked)}
            />
          </FormGroup>
        </div>
      )}

      <div className={classes.wrapperActions}>
        <PF2MGenericButton
          className={classes.buttonActions}
          onClick={handleDiscard}
          startIcon={<DeleteOutlined fontSize="small" />}
        >
          {translate('common:DiscardChanges')}
        </PF2MGenericButton>
        <PF2MGenericButton
          variant={'contained'}
          color={'secondary'}
          className={classes.buttonActions}
          onClick={handleContinue}
          startIcon={<ArrowForwardOutlined fontSize="small" />}
          disabled={!formIsValid || !hasChanged}
        >
          {translate('common:Continue')}
        </PF2MGenericButton>
      </div>
    </div>
  );
};

export default StatusTab;

StatusTab.propTypes = {
  classes: PropTypes.object.isRequired,
  currentModalStates: PropTypes.object.isRequired,
  setStatusState: PropTypes.func.isRequired,
  lookupCtx: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};
